import React, { useRef, useCallback } from 'react';
import './Services.css';
import serviceHospitalityImg from '../assets/ServiceHospitality_4k.webp';
import serviceOfficeImg from '../assets/OfficeComplex/o1.webp';
import serviceVillaImg from '../assets/LuxuryClassicalVilla/v1.webp';
import serviceApartmentImg from '../assets/apartment/a1.webp';
import serviceClinicImg from '../assets/trudent_v1/x1.webp';
import serviceDesignImg from '../assets/AboutStudio_4k.webp';

const services = [
  {
    title: "Architectural Design & Execution",
    description: "End-to-end building solutions from concept to construction.",
    image: serviceOfficeImg,
    features: ["Concept Design", "Sanction Drawings", "Construction Detailing", "PMC Consultancy"]
  },
  {
    title: "Interior Design & Turnkey Solutions",
    description: "Bespoke interior solutions crafted to reflect opulent living.",
    image: serviceApartmentImg,
    features: ["Bespoke Styling", "Modular Furniture", "Turnkey Execution"]
  },
  {
    title: "3D Spatial Visualization & VR",
    description: "Immersive 3D renders, VR walkthroughs, and BIM modeling.",
    image: serviceDesignImg,
    features: ["3D Renders & Walkthroughs", "VR Presentations", "BIM Modeling", "Smart Automation"]
  },
  {
    title: "Hospitality & Boutique Design",
    description: "Immersive guest experiences for hotels, resorts, & lounges.",
    image: serviceHospitalityImg,
    features: ["Hotels & Resorts", "Luxury Lounges", "Guest-Centric Design"]
  },
  {
    title: "Commercial & Healthcare Spaces",
    description: "Optimized spatial layouts for corporate offices and medical clinics.",
    image: serviceClinicImg,
    features: ["Corporate Head Offices", "Medical Clinics", "Ergonomic Layouts"]
  },
  {
    title: "Luxury Residential Architecture",
    description: "Custom villas, penthouses, and gated communities crafted with precision.",
    image: serviceVillaImg,
    features: ["Neo-Classical Villas", "Penthouse Interiors", "Private Residences"]
  }
];

const TiltServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const onMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width  - 0.5;
    const y = (e.clientY - rect.top)  / rect.height - 0.5;
    el.style.transform = `perspective(900px) rotateY(${x * 12}deg) rotateX(${-y * 10}deg) scale(1.03)`;
    el.style.boxShadow = `${-x * 18}px ${y * 18}px 45px rgba(0,0,0,0.30), 0 0 0 1px rgba(212,175,55,0.30)`;
  }, []);
  const onMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'perspective(900px) rotateY(0deg) rotateX(0deg) scale(1)';
    el.style.boxShadow = '';
    el.style.transition = 'transform 0.6s ease, box-shadow 0.6s ease';
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal-depth delay-${(index % 3) + 1} group bg-white/5 border border-amber-300/20 backdrop-blur-xl rounded-2xl overflow-hidden shadow-xl transition-all duration-300 will-change-transform flex flex-col justify-between`}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      <div className="relative h-64 overflow-hidden">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#021a13] via-transparent to-transparent"></div>
      </div>
      <div className="p-6 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="text-xl font-bold font-serif text-white mb-2">{service.title}</h3>
          <p className="text-gray-300 text-sm mb-4 leading-relaxed">{service.description}</p>
        </div>
        <ul className="space-y-2 pt-2 border-t border-amber-300/10">
          {service.features.map((feature, idx) => (
            <li key={idx} className="flex items-center text-xs text-amber-200/90">
              <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mr-2.5 shadow-[0_0_8px_#D4AF37]"></div>
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const Services = () => {
  return (
    <section
      id="services"
      className="py-24 relative overflow-hidden text-white"
      style={{
        backgroundColor: '#021a13',
        background: 'linear-gradient(180deg, #021a13 0%, #04281c 50%, #021a13 100%)',
        color: "#ffffff"
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 reveal-left">
          <p className="font-serif text-xs uppercase tracking-widest text-amber-400 mb-2">Our Capabilities</p>
          <h2 className="text-4xl md:text-5xl font-serif font-normal text-white mb-4">
            Our Services
          </h2>
          <div className="w-20 h-0.5 bg-gradient-to-r from-amber-600 via-amber-400 to-amber-600 mx-auto mb-4"></div>
          <p className="text-base text-gray-300 max-w-2xl mx-auto">
            From initial concept and architectural planning to photorealistic 3D visualization and turnkey execution.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <TiltServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;